// src/encounter/drug-interaction-acknowledge-dialog.tsx
// The forced drug-interaction acknowledgement before a medication is saved (patient safety: restores
// Flutter `NewMedicationAddUpdateWidget.canPop`, new_medication_add_update_widget.dart:1427-1450).
// PRESENTATIONAL — the DS `Modal` (sm, 400) with two DS `Button`s.
//
// Copy and button words are Flutter's, verbatim. Acknowledging saves the medication with the
// interactions on it (`vidalAlertRequest`, the normal A/P save); declining saves nothing.
// First focus is on "Don't Prescribe", so a stray Enter never prescribes; Escape declines (as a
// Flutter dismiss does). The DS `ModalYesNo` focuses its confirm, so it is not used here.
import { useEffect, useRef } from 'react';
import { Button, Modal } from '@scripsteam/scrips-signal-ds';

export const DRUG_INTERACTION_ACK_TITLE = 'This medication causes a drug interaction. Do you really want to prescribe it?';
export const DRUG_INTERACTION_ACK_MESSAGE = 'You might want not to prescribe it and search for another one.';
export const DRUG_INTERACTION_ACK_PROCEED = 'Acknowledge & Proceed';
export const DRUG_INTERACTION_ACK_DECLINE = "Don't Prescribe";

export interface DrugInteractionAcknowledgeDialogProps {
  open: boolean;
  /** "Acknowledge & Proceed": save as today, with the interactions recorded on the medication. */
  onProceed: () => void;
  /** "Don't Prescribe", Escape or a dismiss (as in Flutter): close without saving. */
  onDecline: () => void;
}

export function DrugInteractionAcknowledgeDialog({ open, onProceed, onDecline }: DrugInteractionAcknowledgeDialogProps) {
  const declineRef = useRef<HTMLButtonElement>(null);
  // After the dialog's own focus handling, move first focus to the safe answer.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => declineRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);
  if (!open) return null;
  return (
    <Modal
      open
      size="sm"
      onClose={onDecline}
      title={DRUG_INTERACTION_ACK_TITLE}
      description={DRUG_INTERACTION_ACK_MESSAGE}
      footer={
        <>
          <Button ref={declineRef} variant="secondary" size="sm" autoFocus onClick={onDecline}>
            {DRUG_INTERACTION_ACK_DECLINE}
          </Button>
          <Button size="sm" onClick={onProceed}>
            {DRUG_INTERACTION_ACK_PROCEED}
          </Button>
        </>
      }
    />
  );
}
